import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import { Item } from "../components/Table.js/ModalContainer";
import GenerateReportCompany from "./generatePayroll";
import Spinner from "../../../components/Spinner/index.js";
import Modal from "../../../components/Modal";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../FirebaseConfig.ts";
import { planPagos } from "../../../utils/planPagos.js";

function Compania() {
  const { idCompany } = useParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState("");
  const [month, setMonth] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPayroll, setCurrentPayroll] = useState({});
  const [dragActive, setDragActive] = useState(false);

  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  const { data: companyRes, isFetching: loadingCompany } = useSnapshotWhere(
    "companies",
    {
      filter: {
        firstCondition: "uuid",
        secondCondition: "==",
        thirdCondition: idCompany,
      },
    }
  );

  const { data: emailsRes, isFetching: loadingEmails } = useSnapshotWhere(
    "payrollEmails",
    {
      filter: {
        firstCondition: "idCompany",
        secondCondition: "==",
        thirdCondition: idCompany,
      },
    }
  );

  const { handleUpdate } = useUpdateDoc();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const company = companyRes[0];

  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const uploadFile = async (file, path) => {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const generarPagosMes = async () => {
    setLoading(true);
    try {
      const requestsCollection = collection(db, "requests");
      const companyId =
        hostname === "creditos.sofia.com.co"
          ? "tPxAubnMGdjLnSRk2njL"
          : idCompany;
      const q = query(
        requestsCollection,
        where("companyId", "==", companyId),
        where("status", "==", "aprobado")
      );
      const data = await getDocs(q);
      const dataArray = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

      for (let index = 0; index < dataArray.length; index++) {
        const el = dataArray[index];
        const { schedule } = planPagos(el);
        const currentPayment =
          schedule[el.payments ? el.payments.length : 0] || {};
        await handleUpdate("requests", el.uuid, {
          payments: el?.payments
            ? [...el.payments, `${currentPayment.cuota}`]
            : [`${currentPayment.cuota}`],
        });
      }

      await handleUpdate("payrollEmails", currentPayroll.uuid, {
        statusPayment: true,
      });

      setCurrentPayroll({});
      setShowModal(false);
    } catch (error) {
      console.error("Error processing payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveToFirestore = async (urlFile) => {
    try {
      const res = await addDoc(collection(db, "payrollEmails"), {
        idCompany,
        month,
        urlFile,
        createdAt: new Date(),
        statusPayment: false,
      });
      handleUpdate("payrollEmails", res.id, { uuid: res.id });
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      throw error;
    }
  };

  const handleSendPayroll = async (fileUrl) => {
    try {
      const response = await fetch("https://www.lendy.com.co/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: "christian@grupogemcorp.com",
          subject: "Nuevo Reporte de Cobro - Lendy",
          message: `Hola, tienes un nuevo reporte de cobro para tus empleados en Gemcorp SAS, descarga tu archivo aquí: ${fileUrl}`,
        }),
      });
      if (!response.ok) {
        throw new Error("Error sending email");
      }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setError("");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Por favor selecciona un archivo");
      return;
    }
    if (!month) {
      setError("Por favor selecciona un mes");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const filePath = `companies/${idCompany}/payrolls/${month}_${Date.now()}`;
      const fileUrl = await uploadFile(file, filePath);
      await saveToFirestore(fileUrl);
      await handleSendPayroll(fileUrl);

      setUploadSuccess(true);
      setFile(null);
      setMonth("");

      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      console.error("Error in upload process:", error);
      setError("Error al subir el archivo. Intenta de nuevo.");
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    return status ? (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
        Pagado
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
        Pendiente
      </span>
    );
  };

  if (loadingCompany) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-4">
          <Spinner />
          <p className="text-gray-600">Cargando información de la empresa...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            className="text-gray-400"
          >
            <path
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">
          No se encontró la empresa
        </h3>
        <p className="text-gray-600">
          La empresa solicitada no existe o no tienes permisos para verla
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight justify-center flex items-center gap-3">
            <div className="p-2 bg-[#49b0a8]/10 rounded-xl">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {company.name_company}
          </h1>
          <p className="text-gray-600">
            Gestión integral de la empresa y procesamiento de nóminas
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <div
          className={`transition-all duration-700 ease-out delay-200 ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Información de la Empresa
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    Representante Legal
                  </label>
                  <p className="text-gray-900 font-medium">
                    {company.name} {company.lastName}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">
                    Email
                  </label>
                  <p className="text-gray-900">{company.email}</p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-600 mb-3">
                  Documentos de la Empresa
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Item href={company.cam_comercio} name="Cámara de Comercio" />
                  <Item href={company.fot_cedula} name="Cédula Rep. Legal" />
                  <Item href={company.rut} name="RUT" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Generate Report */}
        <div
          className={`transition-all duration-700 ease-out delay-300 ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Generar Reporte de Cobro
            </h2>

            <div className="text-center">
              <GenerateReportCompany
                nit={company?.nit}
                name={company?.name_company}
                companyIdentity={company?.uuid}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div
        className={`transition-all duration-700 ease-out delay-400 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="text-[#49b0a8]"
            >
              <path
                d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Subir Archivo de Cobro
          </h2>

          {/* Success/Error Messages */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-red-600 flex-shrink-0"
              >
                <path
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {uploadSuccess && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-green-600 flex-shrink-0"
              >
                <path
                  d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <p className="text-green-800 font-medium">
                  ¡Archivo subido con éxito!
                </p>
                <p className="text-green-700 text-sm">
                  El archivo se ha enviado por correo electrónico
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Month Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Selecciona el mes
                </label>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                >
                  <option value="">Seleccionar mes</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Archivo de cobro
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-4 transition-colors duration-200 ${
                    dragActive
                      ? "border-[#49b0a8] bg-[#49b0a8]/5"
                      : file
                      ? "border-green-300 bg-green-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="text-center">
                    {file ? (
                      <div className="flex items-center justify-center gap-2">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-green-600"
                        >
                          <path
                            d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="text-sm text-green-700 font-medium">
                          {file.name}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="mx-auto text-gray-400 mb-2"
                        >
                          <path
                            d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-[#49b0a8]">
                            Click para subir
                          </span>{" "}
                          o arrastra el archivo
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, DOC, DOCX hasta 10MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isUploading || !file || !month}
              className={`
                w-full group relative overflow-hidden px-6 py-3 rounded-xl font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:scale-95 flex items-center justify-center gap-2
                ${
                  isUploading || !file || !month
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#49b0a8] hover:bg-[#3a8a84] text-white"
                }
              `}
            >
              {!isUploading && file && month && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              )}

              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span className="relative z-10">Subiendo archivo...</span>
                </>
              ) : (
                <>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="relative z-10"
                  >
                    <path
                      d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="relative z-10">Enviar Archivo</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Emails History */}
      <div
        className={`transition-all duration-700 ease-out delay-500 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Historial de Envíos
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Archivos enviados y estado de pagos
            </p>
          </div>

          {loadingEmails ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <Spinner />
                <p className="text-gray-600">Cargando historial...</p>
              </div>
            </div>
          ) : emailsRes?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-gray-400"
                >
                  <path
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                No hay envíos registrados
              </h3>
              <p className="text-gray-600">
                Aún no se han enviado archivos de cobro
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Mes
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Archivo
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {emailsRes?.map((el, index) => (
                    <tr
                      key={el.uuid}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {el.month}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(
                            el.createdAt?.toDate()
                          ).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(el.statusPayment)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <a
                          href={el.urlFile}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-[#49b0a8] hover:text-[#3a8a84] font-medium text-sm transition-colors duration-200"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Ver Archivo
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={
                            el.statusPayment
                              ? () => null
                              : () => {
                                  setShowModal(true);
                                  setCurrentPayroll(el);
                                }
                          }
                          disabled={el.statusPayment}
                          className={`
                            px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200
                            ${
                              el.statusPayment
                                ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                : "bg-[#49b0a8] hover:bg-[#3a8a84] text-white"
                            }
                          `}
                        >
                          {el.statusPayment ? "Pagado" : "Procesar Pago"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Payment Confirmation Modal */}
      <Modal showModal={showModal} setShowModal={setShowModal}>
        <div className="text-center space-y-6">
          <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              className="text-yellow-600"
            >
              <path
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Confirmar Procesamiento de Pagos
            </h3>
            <p className="text-gray-600">
              ¿Estás seguro de procesar todos los pagos del mes de{" "}
              <strong>{currentPayroll.month}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Esta acción actualizará el estado de todas las cuotas de los
              empleados para este mes.
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setShowModal(false)}
              disabled={loading}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              // onClick={generarPagosMes}
              onClick={() => null}
              disabled={loading}
              className={`
                px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2
                ${
                  loading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#49b0a8] hover:bg-[#3a8a84] text-white"
                }
              `}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Confirmar Pago</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Compania;
